import React, { useState, useEffect } from 'react';
import { createExpense } from '../services/api';
import { getSupportedCurrencies } from '../services/settingsApi';

function ManualEntry({ onExpenseAdded, onBack }) {
  const [formData, setFormData] = useState({
    vendor_name: '',
    amount: '',
    date: new Date().toISOString().split('T')[0], // Today's date
    category: '',
    description: '',
    currency_code: 'SGD'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);

  const categories = [
    { value: '', label: 'Auto-categorize (AI will choose)' },
    { value: 'groceries', label: '🛒 Groceries' },
    { value: 'dining', label: '🍽️ Dining' },
    { value: 'transportation', label: '🚗 Transportation' },
    { value: 'entertainment', label: '🎬 Entertainment' },
    { value: 'shopping', label: '🛍️ Shopping' },
    { value: 'utilities', label: '💡 Utilities' },
    { value: 'healthcare', label: '🏥 Healthcare' },
    { value: 'other', label: '📝 Other' }
  ];

  // Load supported currencies on mount
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const data = await getSupportedCurrencies();
        console.log('📊 [ManualEntry] getSupportedCurrencies response:', data);
        console.log('📊 [ManualEntry] data.currencies type:', typeof data.currencies, ', isArray:', Array.isArray(data.currencies));

        if (data.currencies && Array.isArray(data.currencies)) {
          console.log('✅ [ManualEntry] Valid currencies array with', data.currencies.length, 'items');
          setCurrencies(data.currencies);
          // Set default currency if available
          if (data.currencies.length > 0 && !formData.currency_code) {
            setFormData(prev => ({
              ...prev,
              currency_code: data.currencies[0].code
            }));
          }
        } else {
          console.warn('⚠️ [ManualEntry] data.currencies is not a valid array:', data.currencies);
          setCurrencies([{ code: 'USD', name: 'US Dollar', symbol: '$' }]);
        }
      } catch (err) {
        console.error('❌ [ManualEntry] Failed to load currencies:', err);
        // Fall back to USD if fetch fails
        setCurrencies([{ code: 'USD', name: 'US Dollar', symbol: '$' }]);
      } finally {
        setCurrenciesLoading(false);
      }
    };

    loadCurrencies();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear messages when user starts typing
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate required fields
      if (!formData.vendor_name || !formData.amount || !formData.date) {
        throw new Error('Please fill in all required fields');
      }

      // Validate amount
      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Amount must be a positive number');
      }

      // Prepare data
      const expenseData = {
        vendor_name: formData.vendor_name.trim(),
        amount: amount,
        date: formData.date,
        description: formData.description.trim() || undefined,
        category: formData.category || undefined, // Empty string means auto-categorize
        currency_code: formData.currency_code
      };

      const data = await createExpense(expenseData);

      // Success!
      setSuccess(`Expense added successfully! ${!formData.category ? '(Auto-categorized as: ' + data.expense.category + ')' : ''}`);

      // Reset form
      setFormData({
        vendor_name: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        category: '',
        description: '',
        currency_code: formData.currency_code || 'SGD'
      });

      // Notify parent component
      if (onExpenseAdded) {
        onExpenseAdded(data.expense);
      }

    } catch (err) {
      setError(err.message || 'Failed to create expense');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      padding: 'var(--space-6)',
      maxWidth: '600px',
      margin: '0 auto',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-4)',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-2)',
    },
    label: {
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--color-text-primary)',
    },
    required: {
      color: 'var(--color-error)',
      marginLeft: 'var(--space-1)',
    },
    input: {
      padding: 'var(--space-2) var(--space-3)',
      fontSize: 'var(--font-size-base)',
      border: '1px solid var(--color-border-default)',
      borderRadius: 'var(--radius-default)',
      outline: 'none',
      transition: 'var(--transition-normal)',
      fontFamily: 'inherit',
      backgroundColor: 'var(--color-bg-base)',
      color: 'var(--color-text-primary)',
    },
    inputFocus: {
      borderColor: 'var(--color-primary)',
      boxShadow: '0 0 0 2px white, 0 0 0 4px var(--color-primary)',
    },
    textarea: {
      padding: 'var(--space-2) var(--space-3)',
      fontSize: 'var(--font-size-base)',
      border: '1px solid var(--color-border-default)',
      borderRadius: 'var(--radius-default)',
      outline: 'none',
      minHeight: '100px',
      fontFamily: 'inherit',
      resize: 'vertical',
      backgroundColor: 'var(--color-bg-base)',
      color: 'var(--color-text-primary)',
      transition: 'var(--transition-normal)',
    },
    select: {
      padding: 'var(--space-2) var(--space-3)',
      fontSize: 'var(--font-size-base)',
      border: '1px solid var(--color-border-default)',
      borderRadius: 'var(--radius-default)',
      outline: 'none',
      backgroundColor: 'var(--color-bg-base)',
      color: 'var(--color-text-primary)',
      cursor: 'pointer',
      transition: 'var(--transition-normal)',
      fontFamily: 'inherit',
    },
    button: {
      padding: 'var(--space-3) var(--space-6)',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-medium)',
      border: 'none',
      borderRadius: 'var(--radius-lg)',
      cursor: 'pointer',
      transition: 'var(--transition-normal)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'var(--space-2)',
    },
    primaryButton: {
      backgroundColor: 'var(--color-primary)',
      color: 'var(--color-text-inverse)',
    },
    disabledButton: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    error: {
      padding: 'var(--space-3)',
      borderRadius: 'var(--radius-default)',
      backgroundColor: 'var(--color-error-light)',
      color: 'var(--color-error-dark)',
      fontSize: 'var(--font-size-sm)',
    },
    success: {
      padding: 'var(--space-3)',
      borderRadius: 'var(--radius-default)',
      backgroundColor: 'var(--color-success-light)',
      color: 'var(--color-success-dark)',
      fontSize: 'var(--font-size-sm)',
    },
    loader: {
      display: 'inline-block',
      width: '14px',
      height: '14px',
      border: '2px solid var(--color-text-inverse)',
      borderTop: '2px solid transparent',
      borderRadius: 'var(--radius-full)',
      animation: 'spin 0.8s linear infinite',
    },
    hint: {
      fontSize: 'var(--font-size-xs)',
      color: 'var(--color-text-tertiary)',
      marginTop: 'var(--space-2)',
    },
  };

  return (
    <div style={styles.container}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
        <h2 style={{
          fontSize: 'var(--font-size-2xl)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-primary)',
          margin: 0,
          letterSpacing: '-0.025em',
        }}>
          Add Expense Manually
        </h2>
        {onBack && (
          <button 
            onClick={onBack}
            style={{
              padding: '8px 12px',
              backgroundColor: 'transparent',
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-border-default)',
              borderRadius: 'var(--radius-md)',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>
        )}
      </div>
      <p style={{
        fontSize: 'var(--font-size-sm)',
        color: 'var(--color-text-secondary)',
        marginBottom: 'var(--space-6)',
        margin: `0 0 var(--space-6) 0`
      }}>
        Quickly add an expense without uploading a receipt
      </p>

      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formGroup}>
          <label style={styles.label}>
            Vendor/Merchant Name <span style={styles.required}>*</span>
          </label>
          <input
            type="text"
            name="vendor_name"
            value={formData.vendor_name}
            onChange={handleChange}
            placeholder="e.g., Starbucks, Walmart, Shell Gas Station"
            style={styles.input}
            disabled={loading}
            required
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--color-primary)';
              e.target.style.boxShadow = '0 0 0 2px white, 0 0 0 4px var(--color-primary)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--color-border-default)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ ...styles.formGroup, flex: 2, minWidth: '150px' }}>
            <label style={styles.label}>
              Amount <span style={styles.required}>*</span>
            </label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              style={styles.input}
              disabled={loading}
              required
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--color-primary)';
                e.target.style.boxShadow = '0 0 0 2px white, 0 0 0 4px var(--color-primary)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--color-border-default)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div style={{ ...styles.formGroup, flex: 1, minWidth: '150px' }}>
            <label style={styles.label}>
              Currency <span style={styles.required}>*</span>
            </label>
            <select
              name="currency_code"
              value={formData.currency_code}
              onChange={handleChange}
              style={styles.select}
              disabled={loading || currenciesLoading}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--color-primary)';
                e.target.style.boxShadow = '0 0 0 2px white, 0 0 0 4px var(--color-primary)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--color-border-default)';
                e.target.style.boxShadow = 'none';
              }}
            >
              {currenciesLoading ? (
                <option>Loading...</option>
              ) : (
                currencies.map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>
            Date <span style={styles.required}>*</span>
          </label>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            style={styles.input}
            disabled={loading}
            required
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--color-primary)';
              e.target.style.boxShadow = '0 0 0 2px white, 0 0 0 4px var(--color-primary)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--color-border-default)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>
            Category (Optional)
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            style={styles.select}
            disabled={loading}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--color-primary)';
              e.target.style.boxShadow = '0 0 0 2px white, 0 0 0 4px var(--color-primary)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--color-border-default)';
              e.target.style.boxShadow = 'none';
            }}
          >
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
          <div style={styles.hint}>
            Leave empty to let AI automatically categorize based on vendor name
          </div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>
            Description (Optional)
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Add any notes or details about this expense..."
            style={styles.textarea}
            disabled={loading}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--color-primary)';
              e.target.style.boxShadow = '0 0 0 2px white, 0 0 0 4px var(--color-primary)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--color-border-default)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        {error && (
          <div style={styles.error}>
            <strong>⚠️ Error: </strong>{error}
          </div>
        )}

        {success && (
          <div style={styles.success}>
            <strong>✅ Success! </strong>{success}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          style={{
            ...styles.button,
            ...styles.primaryButton,
            ...(loading ? styles.disabledButton : {}),
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = 'var(--color-primary-hover)';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = 'var(--color-primary)';
            }
          }}
        >
          {loading && <div style={styles.loader}></div>}
          {loading ? 'Adding Expense...' : '➕ Add Expense'}
        </button>
      </form>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default ManualEntry;
